import { useEffect, useState, useCallback } from 'react';
import { X } from 'lucide-react';

interface OnboardingSpotlightProps {
  elementId: string | null;
  message?: string | null;
  onClose?: () => void;
}

interface ElementPosition {
  top: number;
  left: number;
  width: number;
  height: number;
}

export function OnboardingSpotlight({ 
  elementId, 
  message, 
  onClose 
}: OnboardingSpotlightProps) {
  const [position, setPosition] = useState<ElementPosition | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  const updatePosition = useCallback(() => {
    if (!elementId) {
      setPosition(null);
      setIsVisible(false);
      return;
    }

    const element = document.querySelector(`[data-onboarding="${elementId}"]`) 
      || document.getElementById(elementId);

    if (element) {
      const rect = element.getBoundingClientRect();
      setPosition({
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        height: rect.height,
      });
      setIsVisible(true);

      // Scroll element into view
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      setPosition(null);
      setIsVisible(false);
    }
  }, [elementId]);

  useEffect(() => {
    updatePosition();

    // Update on resize/scroll
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [updatePosition]);

  if (!isVisible || !position) {
    return null;
  }

  const padding = 8;
  const tooltipOffset = 12;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 z-[9998] pointer-events-none"
        style={{
          background: `radial-gradient(
            ellipse ${position.width + 100}px ${position.height + 100}px at ${position.left + position.width / 2}px ${position.top + position.height / 2}px,
            transparent 0%,
            rgba(0, 0, 0, 0.5) 100%
          )`,
        }}
      />

      {/* Spotlight ring */}
      <div
        className="fixed z-[9999] pointer-events-none rounded-lg ring-4 ring-emerald-400 ring-offset-2 ring-offset-transparent animate-pulse"
        style={{
          top: position.top - padding,
          left: position.left - padding,
          width: position.width + padding * 2,
          height: position.height + padding * 2,
        }}
      />

      {/* Tooltip */}
      {message && (
        <div
          className="fixed z-[10000] max-w-xs bg-white dark:bg-zinc-800 rounded-xl shadow-xl border border-emerald-200 dark:border-emerald-700 p-3"
          style={{
            top: position.top + position.height + tooltipOffset,
            left: Math.max(16, Math.min(
              position.left + position.width / 2 - 150,
              window.innerWidth - 316
            )),
          }}
        >
          <div className="flex items-start gap-2">
            <p className="text-sm text-zinc-700 dark:text-zinc-200 flex-1">
              {message}
            </p>
            {onClose && (
              <button
                onClick={onClose}
                className="flex-shrink-0 p-1 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors"
                aria-label="Fermer"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {/* Arrow */}
          <div 
            className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white dark:bg-zinc-800 border-l border-t border-emerald-200 dark:border-emerald-700 rotate-45"
          />
        </div>
      )}
    </>
  );
}

export default OnboardingSpotlight;
